import type { FunctionComponent } from "react"

import { clsx } from "clsx"

import { Text } from "@jobteaser/spark/components/Text"

import { KeyInformation } from "@/features/companyDetail/components/shared/KeyInformation/KeyInformation"
import { Parity } from "@/features/companyDetail/components/shared/Parity/Parity"
import { getCountryFlagEmoji } from "@/features/companyDetail/utils/getCountryFlagEmoji"
import { useCompanyFetch } from "@/modules/companies/hooks/useCompanyFetch"
import { useTranslation } from "@/modules/i18n/components/useTranslation"
import { useLocale } from "@/modules/locales/useLocale"
import { formatNumber } from "@/modules/number/formatNumber"

import styles from "./TeamsKeyInformation.module.css"

export const TeamsKeyInformation: FunctionComponent = () => {
  const { t } = useTranslation(["companies_client"])
  const locale = useLocale()
  const { dataCompany } = useCompanyFetch()

  if (!dataCompany) return null
  const {
    page: {
      headcount,
      countriesPresence,
      averageAge,
      employeeParity: { men, women, others },
    },
  } = dataCompany

  const showParityValue = men > 0 || women > 0 || others > 0
  const showTeamsKeyInformation = headcount > 0 || countriesPresence.length > 0 || averageAge > 0 || showParityValue
  const isInline = !dataCompany?.page.teamDescription

  return (
    (showTeamsKeyInformation && (
      <ul className={clsx(styles.teamsKeyInformation, isInline && styles["teamsKeyInformation--inline"])}>
        {headcount > 0 && (
          <li className={styles.teamsKeyInformation__key} data-testid="company_teams_key_information_headcount">
            <KeyInformation title={t("companies_client.keyInformation.headcount.title")}>
              <Text variant="body1" weight="bold">
                {formatNumber(headcount, locale)}
              </Text>
            </KeyInformation>
          </li>
        )}
        {countriesPresence.length > 0 && (
          <li
            className={styles.teamsKeyInformation__key}
            data-testid="company_teams_key_information_countries_presence"
          >
            <KeyInformation title={t("companies_client.keyInformation.countriesPresence.title")}>
              <div className={styles.teamsKeyInformation__keyCountry}>
                {countriesPresence.map(country => (
                  <Text key={`country-flag-${country.code}`} variant="body1" weight="bold">
                    {getCountryFlagEmoji(country.code)}
                  </Text>
                ))}
              </div>
            </KeyInformation>
          </li>
        )}
        {averageAge > 0 && (
          <li className={styles.teamsKeyInformation__key} data-testid="company_teams_key_information_average_age">
            <KeyInformation title={t("companies_client.keyInformation.averageAge.title")}>
              <Text variant="body1" weight="bold">
                {t("companies_client.keyInformation.averageAge.age", { age: averageAge })}
              </Text>
            </KeyInformation>
          </li>
        )}
        {showParityValue && (
          <li className={styles.teamsKeyInformation__key} data-testid="company_teams_key_information_parity">
            <KeyInformation title={t("companies_client.keyInformation.parity.title")}>
              <Parity />
            </KeyInformation>
          </li>
        )}
      </ul>
    )) ||
    null
  )
}
